import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle } from 'lucide-react';

const SECTIONS = [
  {
    title: 'About the Challenge',
    items: [
      {
        q: 'Why participate in The Kenya Challenge?',
        a: 'Every step you take helps a Kenyan student stay in school. The Challenge turns your daily walking into real scholarship funding through pledges from your friends and family — while also building a healthy habit and connecting you with a team that shares your goal.',
      },
      {
        q: 'How does my walking help Kenyan students?',
        a: 'Your supporters pledge an amount per kilometer (or a flat total) tied to your progress. As you log kilometers, those pledges convert into donations to the Kenya Education Fund, which pays for tuition, uniforms, books and exam fees for promising students who couldn\u2019t otherwise afford secondary school.',
      },
    ],
  },
  {
    title: 'Logging your activity',
    items: [
      {
        q: 'How do I log my daily activity?',
        a: 'Tap the Activity tab, choose Steps or Kilometers, enter your number, pick the date, and tap Add. Steps are converted to kilometers automatically (1,300 steps \u2248 1 km).',
      },
      {
        q: 'Can I log activity for a previous day?',
        a: 'Yes. Tap the date field on the Activity tab, pick the day, and submit as usual. You can backfill any day in the current challenge window.',
      },
      {
        q: "How do I sync my phone's step count automatically?",
        a: 'On the Activity tab tap \u201cConnect your phone\u201d and grant access when prompted. iOS reads your steps from Apple Health (Motion & Fitness). Android reads from the built-in Step Counter. We never track your GPS or run in the background \u2014 we only read steps you\u2019ve already saved.',
      },
      {
        q: 'Where do I see my history?',
        a: 'Activity tab \u2192 scroll down to Activity History. The last 3 entries show by default \u2014 tap \u201cSee more\u201d to view everything you\u2019ve logged this challenge.',
      },
    ],
  },
  {
    title: 'Supporters and pledges',
    items: [
      {
        q: 'How do I share my fundraising link?',
        a: 'Tap your profile picture (top right of any screen) and choose \u201cShare my fundraising link\u201d. Your phone\u2019s share sheet opens \u2014 pick any app (Messages, Email, WhatsApp) to send your unique link.',
      },
      {
        q: "How do I invite supporters who haven't pledged yet?",
        a: 'Supporters tab \u2192 enter the supporter\u2019s name and email \u2192 tap Invite. They get an SMS/email-ready message with your fundraising link. Invited supporters show up at the bottom of the Supporters tab until they pledge.',
      },
      {
        q: 'How do my supporters actually pledge or donate?',
        a: 'Your fundraising link opens The Kenya Challenge website in their browser, where they can pledge per kilometer or a flat total. All payments are processed securely on the web \u2014 the app itself never handles money or credit card info.',
      },
      {
        q: "Why doesn't my Supporters list show someone I invited?",
        a: 'Once an invitee makes a pledge they move from the \u201cInvited\u201d list into your main Supporters list. If they pledged but you don\u2019t see them yet, pull down on the Supporters tab to refresh.',
      },
    ],
  },
  {
    title: 'Team and Leaderboard',
    items: [
      {
        q: "How do I see my team's progress?",
        a: 'Tap the Team tab. You\u2019ll see your team\u2019s total distance, total raised, and a list of every member with their individual stats. Tap a member to see their fundraising page in the browser.',
      },
      {
        q: 'How does the Leaderboard work?',
        a: 'Leaderboard tab \u2192 use the four buttons to switch between Top Walkers by Distance, Top Fundraisers, Top Teams by Distance, and Top Teams by Fundraising. The list refreshes every few minutes.',
      },
    ],
  },
  {
    title: 'Your account',
    items: [
      {
        q: 'How do I update my profile picture?',
        a: 'Tap your profile picture (top right) \u2192 tap your avatar at the top of the sheet \u2192 pick a photo from your gallery or take a new one with your camera.',
      },
      {
        q: 'What if I forget my password?',
        a: 'On the Sign In screen tap \u201cForgot password?\u201d \u2014 that opens the reset flow on the Kenya Challenge website. Once you\u2019ve reset, come back to the app and sign in with your new password.',
      },
      {
        q: 'How do I sign out?',
        a: 'Profile picture (top right) \u2192 scroll to the bottom of the sheet \u2192 tap Sign Out.',
      },
    ],
  },
  {
    title: 'Privacy and support',
    items: [
      {
        q: 'Does the app track my location?',
        a: 'No. We never use GPS and we never track you in the background. We only read step counts you\u2019ve already saved to Apple Health or your phone\u2019s Step Counter \u2014 and only when you tap Sync.',
      },
      {
        q: 'How do I contact support?',
        a: 'Profile picture (top right) \u2192 Email support. That opens your mail app with the right address pre-filled: monsheila@kenyaeducationfund.org.',
      },
    ],
  },
];

export default function FaqPage() {
  return (
    <div className="container-app py-10 md:py-14 max-w-3xl" data-testid="faq-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-orange-600 mb-6"
        data-testid="faq-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back home
      </Link>

      <div className="flex items-center gap-2 mb-2">
        <HelpCircle className="w-5 h-5 text-orange-600" />
        <span className="text-orange-600 font-medium text-xs tracking-wider uppercase">FAQs</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">
        The Kenya Challenge &mdash; FAQs
      </h1>
      <p className="text-stone-500 mb-10">
        Everything you need to know to walk, log, raise &mdash; and stay in the loop.
      </p>

      <div className="space-y-10">
        {SECTIONS.map((section) => (
          <section key={section.title} data-testid={`faq-section-${slugify(section.title)}`}>
            <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">
              {section.title}
            </h2>
            <div className="space-y-5">
              {section.items.map((item) => (
                <div
                  key={item.q}
                  className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-5"
                >
                  <p className="text-sm font-bold text-stone-900 mb-1.5">{item.q}</p>
                  <p className="text-sm text-stone-700 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function slugify(s) {
  return s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}
